import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Grid,
  Button,
  Tooltip
} from "@mui/material";

import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";

export default function SurveyorCamera({
  localVideoRef,
  videos,
  recordedVideos,
  capturedImages,
  setCapturedImages,
  setVideos,
  isMuted,
  isVideoOff,
  toggleMute,
  toggleVideo,
  setOpenGallery
}) {

  const containerRef = useRef(null);

  const [videoBounds, setVideoBounds] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const handleResize = () => {
      updateVideoBounds();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);


  // ✅ Calculate actual visible video area (IMPORTANT FIX)
  const updateVideoBounds = () => {
    const video = localVideoRef?.current;
    const container = containerRef?.current;

    if (!video || !container) return;

    const containerRect = container.getBoundingClientRect();

    const videoRatio = video.videoWidth / video.videoHeight;
    const containerRatio = containerRect.width / containerRect.height;

    let width, height, left, top;

    if (videoRatio > containerRatio) {
      // letterbox top/bottom
      width = containerRect.width;
      height = width / videoRatio;
      left = 0;
      top = (containerRect.height - height) / 2;
    } else {
      // letterbox left/right
      height = containerRect.height;
      width = height * videoRatio;
      top = 0;
      left = (containerRect.width - width) / 2;
    }

    setVideoBounds({ top, left, width, height });
  };

  return (
    <Box sx={{ flex: { xs: 1, md: 1 }, minHeight: 200 }}>
      <Paper sx={{ height: "100%", display: "flex", flexDirection: "column", p: 1 }}>

        {/* HEADER */}
        <Box
          sx={{
            p: 1,
            bgcolor: "#eee",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <Typography variant="h6">Surveyor Camera</Typography>

          <Button
            variant="contained"
            size="small"
            onClick={() => setOpenGallery(true)}
          >
            View
          </Button>
        </Box>

        {/* 🖼️ IMAGES */}
        <Box
          sx={{
            flex: 1,
            bgcolor: "#ddd",
            p: 1,
            overflowY: "auto",
            border: "1px solid #bbb",
            borderRadius: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="caption" sx={{ mb: 0.5 }}>
            Images
          </Typography>

          {capturedImages?.length > 0 ? (
            <Grid container spacing={1}>
              {capturedImages.map((img, index) => (
                <Grid item xs={6} key={index}>
                  <Box
                    component="img"
                    src={img}
                    sx={{
                      width: "100%",
                      height: 80,
                      objectFit: "cover",
                      borderRadius: 1,
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Typography variant="caption">No images</Typography>
            </Box>
          )}
        </Box>

        {/* 🎥 VIDEOS */}
        <Box
          sx={{
            flex: 1,
            bgcolor: "#ddd",
            p: 1,
            overflowY: "auto",
            border: "1px solid #bbb",
            borderRadius: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="caption" sx={{ mb: 0.5 }}>
            Videos
          </Typography>

          {[...(recordedVideos || []), ...(videos || [])].length > 0 ? (
            <Grid container spacing={1}>
              {[...(recordedVideos || []), ...(videos || [])].map((vid, index) => (
                <Grid item xs={6} key={index}>
                  <video
                    src={vid}
                    controls
                    preload="metadata"
                    onLoadedMetadata={updateVideoBounds}
                    muted
                    playsInline
                    style={{
                      width: "100%",
                      height: 80,
                      objectFit: "cover",
                      borderRadius: 4,
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Typography variant="caption">No videos</Typography>
            </Box>
          )}
        </Box>

        {/* 🎬 LIVE CAMERA */}
        <Box
          ref={containerRef}
          sx={{
            flex: 1,
            position: "relative",
            bgcolor: "#000",
            borderRadius: 1,
            border: "1px solid #bbb",
            overflow: "hidden",
          }}
        >
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            onLoadedMetadata={updateVideoBounds}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              transform: "scaleX(-1)",
            }}
          />

          {/* 🎯 OVERLAY WRAPPER (INSIDE VIDEO ONLY) */}
          <Box
            sx={{
              position: "absolute",
              top: videoBounds.top,
              left: videoBounds.left,
              width: videoBounds.width,
              height: videoBounds.height,
              pointerEvents: "none",
            }}
          >

            {/* VIDEO OFF */}
            {isVideoOff && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  bgcolor: "rgba(0,0,0,0.7)",
                }}
              >
                <VideocamOffIcon sx={{ color: "#fff", fontSize: 40, mb: 1 }} />
                <Typography sx={{ color: "#fff" }}>Video Off</Typography>
              </Box>
            )}

            {/* LABEL */}
            <Typography
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                bgcolor: "#555",
                color: "#fff",
                px: 1,
                borderRadius: 1,
                fontSize: 12,
              }}
            >
              Surveyor
            </Typography>

          </Box>

          {/* CONTROLS (clickable, outside pointerEvents none) */}
          <Box sx={{ position: "absolute", bottom: 8, left: 8, zIndex: 3 }}>
            <Tooltip title={isMuted ? "Unmute" : "Mute"}>
              <IconButton
                onClick={toggleMute}
                sx={{
                  bgcolor: isMuted ? "error.main" : "rgba(0,0,0,0.5)",
                  color: "#fff",
                }}
              >
                {isMuted ? <MicOffIcon /> : <MicIcon />}
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ position: "absolute", bottom: 8, right: 8, zIndex: 3 }}>
            <Tooltip title={isVideoOff ? "Turn Video On" : "Turn Video Off"}>
              <IconButton
                onClick={toggleVideo}
                sx={{
                  bgcolor: isVideoOff ? "error.main" : "rgba(0,0,0,0.5)",
                  color: "#fff",
                }}
              >
                {isVideoOff ? <VideocamOffIcon /> : <VideocamIcon />}
              </IconButton>
            </Tooltip>
          </Box>

        </Box>
      </Paper>
    </Box>
  );
}